/* ============================================================================
   RUB HUB — Desktop banner text auto-fit
   ============================================================================
   v1 (2026-08-29, Floody: "text clash on Umami Port & Black Garlic — text
   should be centered between the bottom of the badges and the top of the
   Shop Now button, this should be the rule when it's too large").

   CONTEXT: the 2026-08-26 "DESKTOP TEXT BOX" CSS fix (see
   sections/rh-collection-brand-banner.liquid, .rh-cbb__slide--hz
   .rh-cbb__slidetext under @media (min-width:750px)) already gives the
   heading+tagline+blurb stack a real bounded, vertically-centered box
   between the badge and the Shop Now button (top:30%; bottom:22%;
   display:flex; flex-direction:column; justify-content:center;
   overflow:hidden). That solved the POSITION half of the problem.

   It never solved the SIZE half. Nothing on desktop ever shrinks the text,
   so a brand with a long product name (2-line heading) plus a full
   sentence-length blurb can still be taller than the box. overflow:hidden
   then silently guillotines the last line mid-sentence right at the box's
   own bottom edge — which sits just above the Shop Now button, so the
   clipped sliver reads exactly like the text is clashing with the button.
   Confirmed live on RUM AND QUE: Umami Port & Black Garlic — 2-line heading
   + tagline + 3-line blurb overflowed the box; the blurb's third line was
   guillotined right above the buttons.

   FIX: give desktop the same wrap-then-shrink discipline mobile has had
   since v5/v6 (assets/rh-mobile-slidetext-autofit.js) — shrink each tier
   to fit its own max line count, then if the whole stack still overflows
   the box, scale all three tiers down together by ONE shared multiplier
   (preserves the heading/tagline/blurb type hierarchy) until it fits.
   Mirrors mobile's HEADING_MAX_LINES=2 / SUBHEADING_MAX_LINES=1 line
   budget (assets/rh-mobile-slidetext-autofit.js v8) plus a 3-line budget
   for the desktop blurb, so a brand's copy reads by the same rule at every
   breakpoint. This is the standing rule going forward: whatever combination
   of heading/tagline/blurb length a brand has, the block centers itself in
   the room between the badge and the Shop Now button and shrinks — never
   clips — when that room isn't enough at natural size.

   Desktop's elements are never -webkit-line-clamp'd (that's a mobile-only
   CSS mechanism, see rh-mobile-banner-overlay-master.css), so overflow
   measurement here is simpler than mobile's: no clamp to lift before
   measuring scrollHeight.

   Runs ONLY on .rh-cbb__slide--hz slides at width > 749px — zero effect on
   legacy (non-hero-zone) slides or on mobile, which keep their own,
   separately-tuned autofit system (rh-mobile-slidetext-autofit.js)
   untouched.

   ----------------------------------------------------------------------
   v2 (2026-08-29, same-day follow-up — Elk Creek heading/subheading
   overlap): Floody asked to run the full Platinum Standard across Elk
   Creek. Regression-checking the brand surfaced a second, separate desktop
   text defect: on Elk Creek (font Bevan, weight_band "black", every ACTIVE
   product's title short enough to render as a single-line heading at close
   to its 68px clamp ceiling) the subheading renders partly UNDER the
   heading's own text-shadow/stroke — confirmed live via Chrome, e.g.
   "CAJUN STINGER SEASONING" / "Louisiana Spice With A Sting".

   Root cause: the CSS gap between heading and subheading
   (.rh-cbb__slidetext-subheading{ margin: 0.57cqw 0 0 }) is purely
   proportional to the container width, while the heading's visual ink
   extends past its layout box by several FIXED CSS pixels: a 1.5px stroke,
   a "6px 6px 3px" hard drop shadow, AND a wider "0 0 10px" blurred halo
   (rgba(0,0,0,.35)) that spreads on every side including straight down.
   None of that shrinks with a smaller font or a narrower container, so a
   brand whose heading renders large because its product names are SHORT
   (one line, near the clamp ceiling, so line-height:1.05 leaves little
   slack) can have a proportional 0.57cqw gap that's thinner than the
   combined shadow+blur bleed, and the subheading visually collides with
   it. Independent of the v1 fit logic above (which only kicks in when a
   heading needs to SHRINK to fit 2 lines or the block height — a short,
   comfortably-1-line heading like Elk Creek's never triggers it).

   Fix: enforce a minimum fixed-px clearance between the heading and
   subheading, layered on top of the existing proportional gap, via inline
   style (same mechanism this script already uses for font-size) rather
   than editing the shared CSS in rh-collection-brand-banner.liquid — this
   keeps the fix inside one already-owned, already-reviewed asset instead
   of a second large-blast-radius file edit. 16px (not 8px — the first pass
   at 8px was tested live and still left a visible touch at the worst-case
   blur-halo edge, so the buffer was widened to comfortably clear the full
   stroke+shadow+blur stack, not just the hard-edged drop shadow alone).
   Sitewide / brand-agnostic: applies to every hero-zone desktop slide, not
   just Elk Creek — a bigger gap is never wrong for a brand that wasn't
   hitting the bug, it just adds a little unused headroom inside the
   already-generous badge-to-button box (which itself absorbs it via
   flexbox centering, and the block-scale step below still runs afterwards
   in case the extra gap pushes a long blurb into overflow). Standing-rule
   status as v1: no per-brand override, applies identically everywhere
   this script runs.

   ----------------------------------------------------------------------
   v3 (2026-08-29, same-day follow-up — Floody: "the tagline is resizing
   incorrectly"): shrinkToFitLines()'s per-1px decrement loop checked the
   floor (size > minFontPx) BEFORE each decrement, not after — so its final
   step could legally undercut the stated minimum by just under 1px (e.g.
   the 12px SUBHEADING_MIN_FONT_PX floor let the live tagline land at
   ~11.15px on Elk Creek's collection page, confirmed via computed style —
   every one of the 8 active product taglines was rendering under its own
   documented floor). Any tier that needed even one shrink pass to fit its
   line budget was exposed — heading, subheading, and blurb all had the
   same off-by-one — but the subheading (tagline) showed it most visibly
   since its floor sits closest to its typical natural size. fitBlockHeight()'s
   own Math.max(t.min, ...) floor was already correct; only this per-tier
   decrement loop had the boundary bug. Fix: clamp size back up to
   minFontPx after the loop exits, so the stated floor is a hard floor
   again, not just an approximate one.

   ----------------------------------------------------------------------
   v4 (2026-08-29, same-day follow-up — Floody: "the tagline is still too
   tiny... it's not matching the headline properly the way it's meant
   to"): v3's floor-clamp was correct as far as it went, but it treated
   the symptom, not the disease — every tagline was hitting the v3 fix's
   12px floor because shrinkToFitLines() was shrinking it there on EVERY
   slide regardless of how short the tagline text was, which the floor
   clamp alone could never explain by itself. Root cause, confirmed live:
   naturalLines()'s scrollHeight/lineHeight heuristic overestimates line
   count for this brand's display fonts by a roughly constant proportional
   margin — measured on Elk Creek's "Tangy, Sticky, Built For Wings" (a
   string that visibly renders as one line with room to spare): scrollHeight
   43px vs a 35.8px lineHeight box (line-height:1.15) at the CSS-natural
   31.15px font-size, a computed "1.2 lines" against the maxLines+0.05=1.05
   threshold for the tagline's MAX_LINES=1 budget — so the loop shrinks it
   as if it were genuinely overflowing. Because both scrollHeight and
   lineHeight scale together with font-size, that ~1.2 ratio stays roughly
   constant all the way down, so the loop never satisfies its exit
   condition and always bottoms out at the floor — this is why EVERY
   tagline on EVERY hero-zone slide was landing at exactly the same tiny
   size regardless of its own text length. HEADING_MAX_LINES=2 and
   BLURB_MAX_LINES=3 have enough budget to absorb the same ~0.2-line
   overshoot without ever tripping their own threshold, which is why only
   the tagline tier showed this defect.

   Fix: for the maxLines===1 tier only (today, just the tagline), replace
   the height-heuristic overflow test with fitsOneLine() — an unambiguous,
   font-metric-proof check of whether the text's true unwrapped width
   (scrollWidth under a temporary white-space:nowrap) exceeds the element's
   own already-rendered width. This is ground truth for "does this actually
   need to wrap", immune to any font's ascender/descender/line-height
   quirks. Heading and blurb keep the untouched naturalLines() path — they
   were never miscounting in a way that produced a visible defect, so
   there's no reason to touch a mechanism that's tuned to their un-tripped
   thresholds. Expected effect once shipped: Elk Creek's tagline renders at
   its CSS-natural clamp size (~31px, close to its 32px ceiling) instead of
   the artificial 12px floor, visibly proportionate to the ~61px heading —
   to be confirmed against the live computed style after deploy. */
(function () {
  var DESKTOP_BREAKPOINT = 749;
  var HEADING_MIN_FONT_PX = 20;
  var SUBHEADING_MIN_FONT_PX = 12;
  var BLURB_MIN_FONT_PX = 10;

  var HEADING_MAX_LINES = 2;
  var SUBHEADING_MAX_LINES = 1;
  var BLURB_MAX_LINES = 3;

  var BLOCK_SCALE_STEP = 0.03;
  var BLOCK_SCALE_FLOOR = 0.55;
  var GUARD = 40;

  /* v2: fixed-px floor added on top of the subheading's own 0.57cqw CSS
     margin-top, sized to comfortably clear the heading's worst-case
     stroke+shadow+blur bleed (1.5px stroke + a 6px/3px-blur drop shadow +
     a wider 10px blurred halo -- 8px alone left a visible touch live on
     Elk Creek, so this is set with real headroom above that). */
  var SUBHEAD_GAP_BUFFER_PX = 16;

  function naturalLines(el) {
    var cs = getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    if (!lh || isNaN(lh)) lh = parseFloat(cs.fontSize) * 1.2;
    return el.scrollHeight / lh;
  }

  /* v4: ground-truth "does this actually wrap" check, used only for the
     maxLines===1 tier (the tagline). See the v4 changelog entry above for
     why naturalLines()'s scrollHeight/lineHeight heuristic cannot be
     trusted at maxLines===1. Caches the element's own rendered width
     BEFORE mutating anything, then forces white-space:nowrap to read the
     text's true unwrapped width via scrollWidth, then restores — a block
     element's own width never depends on its content, so this is safe
     regardless of what triggered a reflow in between. */
  function fitsOneLine(el) {
    var available = el.getBoundingClientRect().width;
    var prevWhiteSpace = el.style.whiteSpace;
    el.style.whiteSpace = 'nowrap';
    var natural = el.scrollWidth;
    el.style.whiteSpace = prevWhiteSpace;
    return natural <= available + 0.5;
  }

  function shrinkToFitLines(el, minFontPx, maxLines) {
    if (!el) return;
    el.style.fontSize = '';
    var natural = parseFloat(getComputedStyle(el).fontSize);
    if (!natural) return;
    var size = natural;
    var guard = 0;
    var fits = maxLines === 1
      ? function () { return fitsOneLine(el); }
      : function () { return naturalLines(el) <= maxLines + 0.05; };
    while (size > minFontPx && guard < 60) {
      if (fits()) break;
      size -= 1;
      el.style.fontSize = size + 'px';
      guard++;
    }
    /* v3: the loop above can legally step size below minFontPx on its
       final iteration (it checks the floor before decrementing, not
       after) — clamp back up so the stated floor actually holds. */
    if (size < minFontPx) {
      size = minFontPx;
      el.style.fontSize = size + 'px';
    }
  }

  function blockOverflows(textBox, tiers) {
    var boxBottom = textBox.getBoundingClientRect().bottom;
    var last = null;
    tiers.forEach(function (t) {
      if (t.el && t.el.getBoundingClientRect().height > 0) last = t.el;
    });
    if (!last) return false;
    return last.getBoundingClientRect().bottom > boxBottom + 0.5;
  }

  function fitBlockHeight(textBox, tiers) {
    if (!textBox || !blockOverflows(textBox, tiers)) return;

    var naturals = tiers.map(function (t) {
      return t.el ? parseFloat(getComputedStyle(t.el).fontSize) : 0;
    });

    var scale = 1;
    var guard = 0;
    while (guard < GUARD) {
      if (!blockOverflows(textBox, tiers)) break;
      scale -= BLOCK_SCALE_STEP;
      if (scale < BLOCK_SCALE_FLOOR) break;

      var moved = false;
      tiers.forEach(function (t, i) {
        if (!t.el || !naturals[i]) return;
        var current = parseFloat(t.el.style.fontSize) || naturals[i];
        var next = Math.max(t.min, naturals[i] * scale);
        if (Math.abs(next - current) > 0.01) moved = true;
        t.el.style.fontSize = next + 'px';
      });
      if (!moved) break;
      guard++;
    }
  }

  function fitSlide(slide) {
    if (window.innerWidth <= DESKTOP_BREAKPOINT) return;
    if (!slide.classList.contains('rh-cbb__slide--hz')) return;

    var textBox = slide.querySelector('.rh-cbb__slidetext');
    if (!textBox) return;

    var heading = slide.querySelector('.rh-cbb__slidetext-heading');
    var subheading = slide.querySelector('.rh-cbb__slidetext-subheading');
    var blurb = slide.querySelector('.rh-cbb__slidetext-blurb--desktop');

    [heading, subheading, blurb].forEach(function (el) {
      if (el) { el.style.fontSize = ''; }
    });
    if (subheading) { subheading.style.marginTop = ''; }

    shrinkToFitLines(heading, HEADING_MIN_FONT_PX, HEADING_MAX_LINES);
    shrinkToFitLines(subheading, SUBHEADING_MIN_FONT_PX, SUBHEADING_MAX_LINES);
    shrinkToFitLines(blurb, BLURB_MIN_FONT_PX, BLURB_MAX_LINES);

    if (subheading) {
      subheading.style.marginTop = 'calc(0.57cqw + ' + SUBHEAD_GAP_BUFFER_PX + 'px)';
    }

    fitBlockHeight(textBox, [
      { el: heading,    min: HEADING_MIN_FONT_PX },
      { el: subheading, min: SUBHEADING_MIN_FONT_PX },
      { el: blurb,      min: BLURB_MIN_FONT_PX }
    ]);
  }

  function fitAll() {
    document.querySelectorAll('.rh-cbb__slide').forEach(function (slide) {
      if (slide.getBoundingClientRect().width < 10) return;
      fitSlide(slide);
    });
  }

  function wireImageLoad() {
    document.querySelectorAll('.rh-cbb__product-overlay').forEach(function (img) {
      if (img.hasAttribute('data-rh-desktop-autofit-bound')) return;
      img.setAttribute('data-rh-desktop-autofit-bound', 'true');
      if (img.complete && img.naturalWidth > 0) return;
      img.addEventListener('load', function () {
        var slide = img.closest('.rh-cbb__slide');
        if (slide) fitSlide(slide);
      });
    });
  }

  function init() {
    fitAll();
    wireImageLoad();
    window.addEventListener('resize', fitAll);
    document.querySelectorAll('.rh-cbb__slider').forEach(function (slider) {
      var obs = new MutationObserver(function () {
        fitAll();
        wireImageLoad();
      });
      obs.observe(slider, { attributes: true, attributeFilter: ['class'], subtree: true });
    });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(fitAll);
    }
    setTimeout(fitAll, 150);
    setTimeout(fitAll, 600);
  }

  window.rhDesktopSlidetextFit = fitAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
